import { useState } from 'react';
import { DashboardPageFormBody, DashboardPageFormShell } from '@shigo-ui/dashboard-ui';
import { Button, ConfirmDialog, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { dashboardT } from '../../../../../lib/messages';
import type { AssignableShigoRole } from '../../../../auth/permissions';
import { formatShigoRole } from '../../../../auth/roleLabels';
import { useNetworkMutations, useNetworkQuery } from '../../hooks';
import type { NetworkBrandAssetFiles, NetworkMutationPayload } from '../../model';
import {
  emptyNetworkFormValue,
  networkToFormValue,
  serializeNetworkFormValue,
  type NetworkFormValue,
} from '../../networkFormSchema';
import { NetworkEditorForm } from './NetworkEditorForm';

export function NetworkEditorPage({ networkId }: { networkId?: string }) {
  const { returnTo } = useReturnNavigation({ to: '/networks' });
  const [pendingRoleRemoval, setPendingRoleRemoval] = useState<{
    payload: NetworkMutationPayload;
    assets: NetworkBrandAssetFiles;
    roles: AssignableShigoRole[];
  } | null>(null);
  const query = useNetworkQuery(networkId);
  const mutations = useNetworkMutations();
  const editing = Boolean(networkId);
  const saving = mutations.create.isPending || mutations.update.isPending;
  const waitingForFreshDetail = editing && !query.isFetchedAfterMount;
  const goBack = () => void returnTo();

  async function finishSave() {
    toast.success(dashboardT(editing ? 'networks.updatedSuccess' : 'networks.createdSuccess'));
    try {
      // The save settled the form, so the unsaved-changes guard must not ask again.
      await returnTo({ replace: true, ignoreBlocker: true });
    } catch {
      toast.error(dashboardT('networks.redirectError'));
    }
  }

  async function updateNetwork(payload: NetworkMutationPayload, assets: NetworkBrandAssetFiles) {
    try {
      if (!networkId) return;
      await mutations.update.mutateAsync({ id: networkId, payload, assets });
    } catch (error) {
      showRequestError(error, dashboardT('networks.saveError'));
      return false;
    }
    await finishSave();
    return true;
  }

  async function save(value: NetworkFormValue) {
    const payload = serializeNetworkFormValue(value, editing);
    const assets = {
      logo: value.brandDetails.logoFile,
      logoLight: value.brandDetails.logoLightFile,
    };
    if (networkId) {
      const savedRoles = query.data?.allowedRoles ?? [];
      const keptRoles = new Set(payload.allowedRoles);
      const removedRoles = savedRoles.filter((role) => !keptRoles.has(role));
      if (removedRoles.length > 0) {
        setPendingRoleRemoval({ payload, assets, roles: removedRoles });
        return;
      }
      await updateNetwork(payload, assets);
      return;
    }

    try {
      await mutations.create.mutateAsync({ payload, assets });
    } catch (error) {
      showRequestError(error, dashboardT('networks.saveError'));
      return;
    }
    await finishSave();
  }

  let content = null;
  if (waitingForFreshDetail && !query.isError) {
    content = (
      <div
        role="status"
        aria-label={dashboardT('networks.loadingDetails')}
        className="grid gap-4 p-6"
      >
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-80" />
      </div>
    );
  } else if (editing && query.isError) {
    content = (
      <DashboardPageFormBody title={dashboardT('networks.loadDetailsError')}>
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          {dashboardT('networks.retry')}
        </Button>
      </DashboardPageFormBody>
    );
  } else if (!editing || query.data) {
    content = (
      <NetworkEditorForm
        key={networkId ?? 'new'}
        initialValue={query.data ? networkToFormValue(query.data) : emptyNetworkFormValue()}
        editing={editing}
        saving={saving}
        onCancel={goBack}
        onSubmit={save}
      />
    );
  }

  return (
    <>
      <DashboardPageFormShell
        title={dashboardT(editing ? 'networks.editTitle' : 'networks.createTitle')}
        backAction={
          <ReturnBackLink fallback={{ to: '/networks' }} label={dashboardT('networks.back')} />
        }
      >
        {content}
      </DashboardPageFormShell>
      <ConfirmDialog
        open={Boolean(pendingRoleRemoval)}
        onOpenChange={(open) => {
          if (!open) setPendingRoleRemoval(null);
        }}
        title={dashboardT('networks.rolesRemovalTitle')}
        description={dashboardT('networks.rolesRemovalConfirm', {
          roles: pendingRoleRemoval?.roles.map(formatShigoRole).join(', ') ?? '',
        })}
        cancelLabel={dashboardT('networks.rolesRemovalCancel')}
        confirmLabel={dashboardT('networks.rolesRemovalAction')}
        pending={saving}
        destructive
        onConfirm={() => {
          if (!pendingRoleRemoval) return;
          void updateNetwork(pendingRoleRemoval.payload, pendingRoleRemoval.assets).then(
            (saved) => {
              if (saved) setPendingRoleRemoval(null);
            },
          );
        }}
      />
    </>
  );
}
